import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

export const ApexPrimeNgPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#effcff',
      100: '#d7f6ff',
      200: '#afefff',
      300: '#73e3ff',
      400: '#35c0ef',
      500: '#2fc1f1',
      600: '#0799cb',
      700: '#087aa4',
      800: '#0c6385',
      900: '#104f6d',
      950: '#08354a',
    },
    colorScheme: {
      light: {
        primary: {
          color: 'var(--primary-color, var(--workspace-primary, #2fc1f1))',
          contrastColor: 'var(--accent-contrast, #ffffff)',
          hoverColor: 'color-mix(in srgb, var(--primary-color, var(--workspace-primary, #2fc1f1)) 86%, #000000)',
          activeColor: 'color-mix(in srgb, var(--primary-color, var(--workspace-primary, #2fc1f1)) 74%, #000000)',
        },
        highlight: {
          background: 'var(--surface-selected, #e0f7ff)',
          focusBackground: 'var(--surface-active, #ccf2ff)',
          color: 'var(--primary-accent, #087aa4)',
          focusColor: 'var(--primary-accent, #087aa4)',
        },
        text: {
          color: 'var(--text-primary, #111827)',
          hoverColor: 'var(--text-primary, #111827)',
          mutedColor: 'var(--text-secondary, #667085)',
          hoverMutedColor: 'var(--text-primary, #111827)',
        },
        content: {
          background: 'var(--surface-card, #ffffff)',
          hoverBackground: 'var(--surface-hover, #f8fafc)',
          borderColor: 'var(--border-default, #e5e7eb)',
          color: 'var(--text-primary, #111827)',
          hoverColor: 'var(--text-primary, #111827)',
        },
        formField: {
          background: 'var(--input-background, #ffffff)',
          disabledBackground: 'var(--surface-active, #eef2f7)',
          filledBackground: 'var(--surface-secondary, #f8fafc)',
          filledHoverBackground: 'var(--surface-hover, #f1f5f9)',
          filledFocusBackground: 'var(--input-background, #ffffff)',
          borderColor: 'var(--input-border, #cbd5e1)',
          hoverBorderColor: 'var(--border-default, #94a3b8)',
          focusBorderColor: 'var(--primary-accent, #2fc1f1)',
          color: 'var(--text-primary, #111827)',
          disabledColor: 'var(--text-disabled, #98a2b3)',
          placeholderColor: 'var(--text-placeholder, #98a2b3)',
        },
        // Options in selects, multiselects, autocompletes and listboxes: hover/keyboard focus and the chosen one.
        list: {
          option: {
            focusBackground: 'var(--surface-hover, #f1f5f9)',
            selectedBackground: 'var(--surface-selected, #e0f7ff)',
            selectedFocusBackground: 'var(--surface-active, #ccf2ff)',
            color: 'var(--text-primary, #111827)',
            focusColor: 'var(--text-primary, #111827)',
            selectedColor: 'var(--primary-accent, #087aa4)',
            selectedFocusColor: 'var(--primary-accent, #087aa4)',
          },
        },
        overlay: {
          select: {
            background: 'var(--surface-overlay, #ffffff)',
            borderColor: 'var(--border-default, #e5e7eb)',
            color: 'var(--text-primary, #111827)',
          },
          popover: {
            background: 'var(--surface-overlay, #ffffff)',
            borderColor: 'var(--border-default, #e5e7eb)',
            color: 'var(--text-primary, #111827)',
          },
          modal: {
            background: 'var(--surface-overlay, #ffffff)',
            borderColor: 'var(--border-default, #e5e7eb)',
            color: 'var(--text-primary, #111827)',
          },
        },
      },
      dark: {
        primary: {
          color: 'var(--primary-accent, var(--workspace-primary, #35c0ef))',
          contrastColor: 'var(--accent-contrast, #04111d)',
          hoverColor: 'color-mix(in srgb, var(--primary-accent, var(--workspace-primary, #35c0ef)) 84%, #ffffff)',
          activeColor: 'color-mix(in srgb, var(--primary-accent, var(--workspace-primary, #35c0ef)) 72%, #ffffff)',
        },
        highlight: {
          background: 'var(--surface-selected, rgba(47, 193, 241, 0.18))',
          focusBackground: 'var(--surface-active, rgba(47, 193, 241, 0.24))',
          color: 'var(--primary-accent, #7ddfff)',
          focusColor: 'var(--primary-accent, #7ddfff)',
        },
        text: {
          color: 'var(--text-primary, #f8fafc)',
          hoverColor: 'var(--text-primary, #f8fafc)',
          mutedColor: 'var(--text-secondary, #cbd5e1)',
          hoverMutedColor: 'var(--text-primary, #f8fafc)',
        },
        content: {
          background: 'var(--surface-card, #111827)',
          hoverBackground: 'var(--surface-hover, #1f2937)',
          borderColor: 'var(--border-default, #334155)',
          color: 'var(--text-primary, #f8fafc)',
          hoverColor: 'var(--text-primary, #f8fafc)',
        },
        formField: {
          background: 'var(--input-background, #0b1220)',
          disabledBackground: 'var(--surface-active, #1f2937)',
          filledBackground: 'var(--surface-secondary, #111827)',
          filledHoverBackground: 'var(--surface-hover, #1f2937)',
          filledFocusBackground: 'var(--input-background, #0b1220)',
          borderColor: 'var(--input-border, #334155)',
          hoverBorderColor: 'var(--border-default, #475569)',
          focusBorderColor: 'var(--primary-accent, #35c0ef)',
          color: 'var(--text-primary, #f8fafc)',
          disabledColor: 'var(--text-disabled, #64748b)',
          placeholderColor: 'var(--text-placeholder, #94a3b8)',
        },
        // Options in selects, multiselects, autocompletes and listboxes: hover/keyboard focus and the chosen one.
        list: {
          option: {
            focusBackground: 'var(--surface-hover, #1f2937)',
            selectedBackground: 'var(--surface-selected, rgba(47, 193, 241, 0.18))',
            selectedFocusBackground: 'var(--surface-active, rgba(47, 193, 241, 0.24))',
            color: 'var(--text-primary, #f8fafc)',
            focusColor: 'var(--text-primary, #f8fafc)',
            selectedColor: 'var(--primary-accent, #7ddfff)',
            selectedFocusColor: 'var(--primary-accent, #7ddfff)',
          },
        },
        overlay: {
          select: {
            background: 'var(--surface-overlay, #111827)',
            borderColor: 'var(--border-default, #334155)',
            color: 'var(--text-primary, #f8fafc)',
          },
          popover: {
            background: 'var(--surface-overlay, #111827)',
            borderColor: 'var(--border-default, #334155)',
            color: 'var(--text-primary, #f8fafc)',
          },
          modal: {
            background: 'var(--surface-overlay, #111827)',
            borderColor: 'var(--border-default, #334155)',
            color: 'var(--text-primary, #f8fafc)',
          },
        },
      },
    },
  },
});
